import { Link, router } from "@inertiajs/react"
import { useEffect, useMemo, useState, type ReactNode } from "react"

import { AdminLayout } from "@/layouts/AdminLayout"
import type {
  QualityFilters,
  QualityIssueCard,
  QualityRegionSummary,
  QualityScanStatus,
  QualitySummary,
  QualityUrls,
} from "@/lib/types"

import { QualitySummaryBar } from "./quality/QualitySummaryBar"
import { FilterBar } from "./quality/FilterBar"
import { Worklist } from "./quality/Worklist"
import { EmptyState } from "./quality/EmptyState"
import { BatchActionsBar } from "./quality/BatchActionsBar"

// T7 → T8: this is the Inertia page rendered by
// Admin::Regions::QualitiesController#show.
//
// Props mirror controller payload exactly. The shape is defined in
// `app/frontend/lib/types.ts` and assertion-tested in
// `test/integration/admin/regions/qualities_controller_test.rb`.
interface Props {
  region: QualityRegionSummary
  summary: QualitySummary
  issues: QualityIssueCard[]
  filters: QualityFilters
  scan_status: QualityScanStatus
  flash: { notice?: string; alert?: string }
  urls: QualityUrls
}

// `flash` is rendered into toasts by AdminLayout's global effect — we do NOT
// double-fire here. The prop is kept on the interface for parity with the
// controller payload + type safety, and is referenced via `_flash` to satisfy
// `noUnusedParameters`.
export default function Quality({
  region,
  summary,
  issues,
  filters,
  scan_status,
  flash: _flash,
  urls,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Clear selection when issue list changes (e.g. after a server round-trip).
  useEffect(() => {
    setSelectedIds(new Set())
  }, [issues])

  const hasFilters = Boolean(filters.issue_type || filters.severity)
  const totalIssues = summary.critical + summary.high + summary.medium
  const scanEverRan = Boolean(scan_status.last_completed_at)

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const clearSelected = () => setSelectedIds(new Set())

  // Empty-state dispatch table — see design spec B6 state matrix.
  const emptyStateKind = useMemo(() => {
    if (issues.length > 0) return null
    if (hasFilters) return "filter_zero" as const
    if (!scanEverRan) return "never_scanned" as const
    return "clean" as const
  }, [issues.length, hasFilters, scanEverRan])

  // Auto-refresh scan status while a scan is in-flight ("queued" by backfill,
  // or "running" inside the scan job). We poll only when the tab is visible
  // (avoid waking sleeping laptops).
  useEffect(() => {
    const inFlight = scan_status.status === "running" || scan_status.status === "queued"
    if (!inFlight) return
    if (typeof document === "undefined") return

    const tick = () => {
      if (document.visibilityState === "visible") {
        router.reload({ only: ["scan_status", "summary", "issues"] })
      }
    }
    const id = window.setInterval(tick, 5000)
    return () => window.clearInterval(id)
  }, [scan_status.status])

  return (
    <div>
      {/* Breadcrumb + title row */}
      <div className="mb-6">
        <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
          <Link href={urls.region} className="hover:underline">
            {region.name}
          </Link>
          <span>/</span>
          <span>Quality</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">Quality &amp; freshness</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Prioritized worklist of issues detected across the region. Apply fixes individually
          or in batch.
        </p>
      </div>

      <QualitySummaryBar
        summary={summary}
        filters={filters}
        scanStatus={scan_status}
        urls={urls}
      />

      <div className="mt-4">
        <FilterBar filters={filters} urls={urls} totalIssues={totalIssues} />
      </div>

      <div className="mt-6 pb-24">
        {emptyStateKind ? (
          <EmptyState kind={emptyStateKind} scanStatus={scan_status} urls={urls} />
        ) : (
          <Worklist
            issues={issues}
            urls={urls}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelected}
          />
        )}
      </div>

      {selectedIds.size > 0 && (
        <BatchActionsBar
          count={selectedIds.size}
          selectedIds={Array.from(selectedIds)}
          urls={urls}
          onClear={clearSelected}
        />
      )}
    </div>
  )
}

Quality.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
